import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Seo } from '@/lib/seo';

/**
 * Branded 404. Replaces the old silent redirect to "/" — a soft-404 that
 * confused visitors and crawlers alike. (True HTTP 404s aren't possible from
 * an SPA; the meaningful part is not pretending the page exists.)
 */
export function NotFoundPage(): JSX.Element {
  return (
    <section className="container flex min-h-[60vh] flex-col items-center justify-center py-14 text-center">
      <Seo
        title="Page not found — 404"
        description="That page doesn't exist. Head back to the homepage, see pricing, or contact us."
        path="/404"
      />
      <p className="text-sm font-semibold uppercase tracking-wide text-[var(--brand-accent)]">
        404
      </p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
        Page not found
      </h1>
      <p className="mt-4 max-w-md text-muted-foreground">
        That link is broken or the page has moved. Nothing missed — your AI
        receptionist would have answered anyway.
      </p>
      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
        <Button asChild>
          <Link to="/">
            Back to homepage <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/pricing">See pricing</Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to="/contact">Contact us</Link>
        </Button>
      </div>
    </section>
  );
}
